import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaUsers, FaChartLine } from 'react-icons/fa';
import Section from '../../components/common/Section';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';

const PageContainer = styled(motion.div)`
  padding-top: 100px;
`;

const HeroSection = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.space['3xl']} 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('/assets/images/pattern.svg');
    opacity: 0.03;
    pointer-events: none;
  }
`;

const HeroTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['5xl']};
  margin-bottom: ${({ theme }) => theme.space.lg};
  position: relative;
  display: inline-block;
  
  span {
    color: ${({ theme }) => theme.colors.accent};
    font-family: ${({ theme }) => theme.fonts.accent};
    font-style: italic;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['4xl']};
  }
`;

const HeroDescription = styled.p`
  max-width: 800px;
  margin: 0 auto ${({ theme }) => theme.space.xl};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const PlatformsSection = styled.div`
  margin-top: ${({ theme }) => theme.space['2xl']};
`;

const PlatformsTitle = styled.h2`
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  margin-bottom: ${({ theme }) => theme.space.xl};
`;

const PlatformsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.space.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const PlatformCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.space.xl};
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const PlatformIcon = styled.div<{ $color: string }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  color: ${({ theme }) => theme.colors.white};
`;

const PlatformTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const PlatformDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.space.xl};
  margin-top: ${({ theme }) => theme.space['3xl']};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ServiceTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  margin-bottom: ${({ theme }) => theme.space.md};
  color: ${({ theme }) => theme.colors.primary};
`;

const ServiceDescription = styled.p`
  margin-bottom: ${({ theme }) => theme.space.lg};
  line-height: 1.7;
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradient.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space.lg};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  color: ${({ theme }) => theme.colors.dark};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
`;

const ProcessSection = styled.div`
  margin-top: ${({ theme }) => theme.space['3xl']};
  background: ${({ theme }) => theme.colors.light};
  padding: ${({ theme }) => theme.space['2xl']} 0;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const ProcessTitle = styled.h2`
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  margin-bottom: ${({ theme }) => theme.space['2xl']};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.space.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ProcessStep = styled.div`
  text-align: center;
  position: relative;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.space.xl};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 40px;
    right: -50%;
    width: 100%;
    height: 2px;
    background: ${({ theme }) => theme.colors.gradient.accent};
    z-index: 0;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
      display: none;
    }
  }
`;

const StepNumber = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradient.primary};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
`;

const StepTitle = styled.h4`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  margin-bottom: ${({ theme }) => theme.space.sm};
  color: ${({ theme }) => theme.colors.primary};
`;

const StepDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const ResultsSection = styled.div`
  margin-top: ${({ theme }) => theme.space['3xl']};
  text-align: center;
`;

const ResultsTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  margin-bottom: ${({ theme }) => theme.space.xl};
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.space.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ResultCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.space.xl};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const ResultNumber = styled.div`
  font-size: ${({ theme }) => theme.fontSizes['5xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: ${({ theme }) => theme.space.sm};
  font-family: ${({ theme }) => theme.fonts.heading};
`;

const ResultText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.space['3xl']};
  padding: ${({ theme }) => theme.space['2xl']} ${({ theme }) => theme.space.xl};
  background: ${({ theme }) => theme.colors.gradient.accent};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.dark};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('/assets/images/pattern.svg');
    opacity: 0.05;
    pointer-events: none;
  }
`;

const CTATitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

const CTADescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  max-width: 800px;
  margin: 0 auto ${({ theme }) => theme.space.xl};
  line-height: 1.7;
`;

const SocialMedia: React.FC = () => {
  return (
    <PageContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Section>
        <HeroSection>
          <HeroTitle>Social Media <span>Management</span></HeroTitle>
          <HeroDescription>
            Build a powerful brand presence across all social platforms with our comprehensive social media management services. Engage your audience, grow your following, and drive meaningful results.
          </HeroDescription>
          <Button variant="accent" size="large">Get Started</Button>
        </HeroSection>
        
        <PlatformsSection>
          <PlatformsTitle>Platforms We Specialize In</PlatformsTitle>
          <PlatformsGrid>
            <PlatformCard>
              <PlatformIcon $color="#3b5998">
                <FaFacebookF />
              </PlatformIcon>
              <PlatformTitle>Facebook</PlatformTitle>
              <PlatformDescription>
                Engage with your audience through compelling content, targeted ads, and community management.
              </PlatformDescription>
            </PlatformCard>
            
            <PlatformCard>
              <PlatformIcon $color="#1da1f2">
                <FaTwitter />
              </PlatformIcon>
              <PlatformTitle>Twitter</PlatformTitle>
              <PlatformDescription>
                Stay relevant with real-time engagement, trending topics, and concise messaging.
              </PlatformDescription>
            </PlatformCard>
            
            <PlatformCard>
              <PlatformIcon $color="#c32aa3">
                <FaInstagram />
              </PlatformIcon>
              <PlatformTitle>Instagram</PlatformTitle>
              <PlatformDescription>
                Showcase your brand visually through stunning imagery, stories, and influencer collaborations.
              </PlatformDescription>
            </PlatformCard>
            
            <PlatformCard>
              <PlatformIcon $color="#0a66c2">
                <FaLinkedinIn />
              </PlatformIcon>
              <PlatformTitle>LinkedIn</PlatformTitle>
              <PlatformDescription>
                Build professional relationships and establish thought leadership in your industry.
              </PlatformDescription>
            </PlatformCard>
          </PlatformsGrid>
        </PlatformsSection>
        
        <ServicesGrid>
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaUsers />
            </IconWrapper>
            <ServiceTitle>Content Creation & Curation</ServiceTitle>
            <ServiceDescription>
              Our team creates engaging, platform-specific content that resonates with your target audience. From eye-catching visuals to compelling copy, we ensure your content stands out.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaChartLine />
            </IconWrapper>
            <ServiceTitle>Community Management</ServiceTitle>
            <ServiceDescription>
              We actively engage with your audience, respond to comments and messages, and foster a sense of community around your brand. Our team ensures timely and authentic interactions.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaUsers />
            </IconWrapper>
            <ServiceTitle>Social Media Advertising</ServiceTitle>
            <ServiceDescription>
              Maximize your reach and ROI with targeted social media advertising campaigns. We develop and optimize ads that drive awareness, engagement, and conversions.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaChartLine />
            </IconWrapper>
            <ServiceTitle>Analytics & Reporting</ServiceTitle>
            <ServiceDescription>
              Track the performance of your social media efforts with comprehensive analytics and regular reporting. We provide actionable insights to continuously improve your strategy.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaUsers />
            </IconWrapper>
            <ServiceTitle>Influencer Partnerships</ServiceTitle>
            <ServiceDescription>
              Connect with relevant influencers in your industry to expand your reach and build credibility. We identify, vet, and manage influencer relationships to ensure authentic collaborations.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaChartLine />
            </IconWrapper>
            <ServiceTitle>Social Strategy Development</ServiceTitle>
            <ServiceDescription>
              Develop a comprehensive social media strategy aligned with your business goals. We create tailored plans that define your voice, content themes, posting schedule, and growth tactics.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
        </ServicesGrid>
        
        <ProcessSection>
          <ProcessTitle>Our Social Media Management Process</ProcessTitle>
          <ProcessSteps>
            <ProcessStep>
              <StepNumber>1</StepNumber>
              <StepTitle>Strategy Development</StepTitle>
              <StepDescription>We create a customized social media strategy based on your goals, audience, and industry.</StepDescription>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>2</StepNumber>
              <StepTitle>Content Creation</StepTitle>
              <StepDescription>Our team develops engaging, platform-specific content that resonates with your audience.</StepDescription>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>3</StepNumber>
              <StepTitle>Community Management</StepTitle>
              <StepDescription>We actively engage with your audience and build a community around your brand.</StepDescription>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>4</StepNumber>
              <StepTitle>Analysis & Optimization</StepTitle>
              <StepDescription>We continuously monitor performance and refine our approach for maximum results.</StepDescription>
            </ProcessStep>
          </ProcessSteps>
        </ProcessSection>
        
        <ResultsSection>
          <ResultsTitle>Results That Speak</ResultsTitle>
          <ResultsGrid>
            <ResultCard>
              <ResultNumber>68%</ResultNumber>
              <ResultText>Average increase in engagement for our clients within the first 90 days</ResultText>
            </ResultCard>
            
            <ResultCard>
              <ResultNumber>3.5x</ResultNumber>
              <ResultText>Average growth in follower count within the first six months</ResultText>
            </ResultCard>
            
            <ResultCard>
              <ResultNumber>42%</ResultNumber>
              <ResultText>Average increase in website traffic from social media channels</ResultText>
            </ResultCard>
          </ResultsGrid>
        </ResultsSection>
        
        <CTASection>
          <CTATitle>Ready to Transform Your Social Media Presence?</CTATitle>
          <CTADescription>
            Let our team of social media experts help you build a strong presence across all platforms, engage your audience, and drive meaningful results. Schedule a consultation today to learn how our tailored approach can work for you.
          </CTADescription>
          <Button variant="primary" size="large">Schedule a Consultation</Button>
        </CTASection>
      </Section>
    </PageContainer>
  );
};

export default SocialMedia;
